import type { ParseKeys } from 'i18next';

import { useTranslation } from 'react-i18next';
import { type UIMatch, useMatches } from 'react-router';

/**
 * A breadcrumb is either a translation `key` (resolved through `t()`) or a raw
 * `literal` string (rendered as-is). Keeping these separate prevents a dynamic
 * value (e.g. a document title or client name) from being accidentally
 * interpreted as a translation key when it happens to match one.
 */
type Breadcrumb = {
  to?: string;
} & ({ label: ParseKeys } | { literal: string });

type Handle = {
  breadcrumb?: (data?: unknown) => Breadcrumb | Breadcrumb[];
};

export const useBreadcrumbs = () => {
  const matches = useMatches() as UIMatch<unknown, Handle>[];

  const { t } = useTranslation();

  return matches
    .filter((match) => match.handle?.breadcrumb)
    .flatMap((match) => {
      const result = match.handle.breadcrumb?.(match.loaderData) ?? [];

      const items = Array.isArray(result) ? result : [result];

      return items.map((item) => ({
        isLiteral: 'literal' in item,
        label: 'literal' in item ? item.literal : t(item.label),
        to: item.to,
      }));
    });
};
